import { computed } from "vue";
import { useContentDeliveryResource } from "../resources/useContentDeliveryResource";
import type { ContentItem, BreadcrumbItem } from "../types";

export type ContentBreadcrumbOptions = {
  excludeTypes?: string[];
};

export async function useContentBreadcrumb ( content: ContentItem | string, options: ContentBreadcrumbOptions = {} ): Promise<Ref<BreadcrumbItem[]>> {
  const {
    getAncestors,
    getById,
  } = useContentDeliveryResource();

  if ( !content ) {
    throw new Error( "Content not provided" );
  }

  if ( typeof content === "string" ) {
    if ( import.meta.dev ) {
      console.warn( "It is recommended to pass a content item to useContentBreadcrumb, not an ID, as this will cause an extra request to the API" );
    }
  }

  const [ancestors, current] = await Promise.all( [
    ( async () => ( await getAncestors( typeof content === "string" ? content : content.id ) ).data.value )(),
    ( async () => typeof content === "string" ? ( await getById( content ) ).data.value : content )(),
  ] );

  return computed( () => {
    const result = [];

    const items = [...ancestors?.items ?? []];
    if ( items.length ) {
      result.push( ...items.reverse()
        .filter( x => !options?.excludeTypes?.includes( x.contentType ) )
        .map( contentItemToBreadcrumb ) );
    }

    if ( current && !options?.excludeTypes?.includes( current.contentType ) ) {
      result.push( contentItemToBreadcrumb( current ) );
    }

    return result;
  } );
}
